/**
 * Pure view-state helpers for the Library screen (screens.md §`/library`):
 * the two lenses (status, type), the three sorts, the grid/list choice —
 * read from and written to the query string — and the per-filter empty copy.
 */

import type { LibraryFilter, LibraryItem, LibraryType, UserShowStatus } from '@shared/types';

import { progressPercent } from '@/lib/library';

export type SortOrder = 'recent' | 'title' | 'progress';
export type ViewMode = 'grid' | 'list';

/** Order is the spec's: All · Watchlist · Watching · Completed · Dropped. */
export const STATUS_FILTERS: LibraryFilter[] = ['all', 'watchlist', 'watching', 'completed', 'dropped'];
export const TYPE_FILTERS: LibraryType[] = ['all', 'show', 'movie'];

/** Keys into the strings catalog — resolve with `t()`. */
export const STATUS_FILTER_LABEL_KEYS: Record<LibraryFilter, string> = {
  all: 'library.filter.all',
  watchlist: 'library.filter.watchlist',
  watching: 'library.filter.watching',
  completed: 'library.filter.completed',
  dropped: 'library.filter.dropped',
};

export const TYPE_FILTER_LABEL_KEYS: Record<LibraryType, string> = {
  all: 'library.type.all',
  show: 'library.type.series',
  movie: 'library.type.movies',
};

export const SORT_LABEL_KEYS: Record<SortOrder, string> = {
  recent: 'library.sort.recent',
  title: 'library.sort.title',
  progress: 'library.sort.progress',
};

/** Per-filter empty copy (ui-ux.md §7) — each lens explains itself. */
export const EMPTY_COPY: Record<LibraryFilter, { overlineKey: string; bodyKey: string }> = {
  all: {
    overlineKey: 'library.empty.all.overline',
    bodyKey: 'library.empty.all.body',
  },
  watchlist: {
    overlineKey: 'library.empty.watchlist.overline',
    bodyKey: 'library.empty.watchlist.body',
  },
  watching: {
    overlineKey: 'library.empty.watching.overline',
    bodyKey: 'library.empty.watching.body',
  },
  completed: {
    overlineKey: 'library.empty.completed.overline',
    bodyKey: 'library.empty.completed.body',
  },
  dropped: {
    overlineKey: 'library.empty.dropped.overline',
    bodyKey: 'library.empty.dropped.body',
  },
};

export function filterLibraryItems(
  items: LibraryItem[],
  status: LibraryFilter,
  type: LibraryType,
): LibraryItem[] {
  return items.filter((it) => {
    if (type !== 'all' && it.title.type !== type) return false;
    return status === 'all' || it.status === status;
  });
}

/** Titles per status filter (and `all`) under one type lens — the toolbar's counts and the masthead summary. */
export function shelfCounts(items: LibraryItem[], type: LibraryType): Record<LibraryFilter, number> {
  const counts: Record<LibraryFilter, number> = { all: 0, watchlist: 0, watching: 0, completed: 0, dropped: 0 };
  for (const it of filterLibraryItems(items, 'all', type)) {
    counts.all += 1;
    if (it.status !== 'removed') counts[it.status] += 1;
  }
  return counts;
}

/**
 * Status marks the wall leaves off (screens.md §`/library`): Completed is
 * the common case, so its seal never shows — a clean cover — and a filter's
 * own status is implied by the filter. The Watching bar stays: it carries
 * progress, not just status.
 */
export function hiddenStatusMarks(filter: LibraryFilter): UserShowStatus[] {
  return filter === 'all' || filter === 'watching' || filter === 'completed'
    ? ['completed']
    : ['completed', filter];
}

/**
 * `recent` keeps the wire order — `/v1/home` is already GSI3 `updated_at`
 * descending. Returns a new array; never mutates the cached query data.
 */
export function sortLibraryItems(items: LibraryItem[], sort: SortOrder): LibraryItem[] {
  const copy = [...items];
  if (sort === 'title') copy.sort((a, b) => a.title.title.localeCompare(b.title.title));
  if (sort === 'progress') copy.sort((a, b) => progressPercent(b) - progressPercent(a));
  return copy;
}

const SORTS: SortOrder[] = ['recent', 'title', 'progress'];
const VIEWS: ViewMode[] = ['grid', 'list'];

/** URL value → known option, anything else → the default. */
function pick<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/**
 * The Library view from `?status=&type=&sort=&view=` (ui-ux.md §3). A missing
 * or unknown value falls back to its default; a missing `view` falls back to
 * the viewer's last grid/list choice (`localStorage`).
 */
export function readLibraryParams(params: URLSearchParams): {
  status: LibraryFilter;
  type: LibraryType;
  sort: SortOrder;
  view: ViewMode;
} {
  return {
    status: pick(params.get('status'), STATUS_FILTERS, 'all'),
    type: pick(params.get('type'), TYPE_FILTERS, 'all'),
    sort: pick(params.get('sort'), SORTS, 'recent'),
    view: pick(params.get('view'), VIEWS, loadViewMode()),
  };
}

const PARAM_DEFAULTS = { status: 'all', type: 'all', sort: 'recent', view: 'grid' } as const;

/** Next query string with one lens set — defaults are omitted from the URL. */
export function writeLibraryParam(
  prev: URLSearchParams,
  key: keyof typeof PARAM_DEFAULTS,
  value: string,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (value === PARAM_DEFAULTS[key]) next.delete(key);
  else next.set(key, value);
  return next;
}

const VIEW_STORAGE_KEY = 'screenatlas.library.view.v1';

export function loadViewMode(): ViewMode {
  try {
    return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

export function saveViewMode(mode: ViewMode): void {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, mode);
  } catch {
    // storage unavailable (private mode) — the toggle still works for the session
  }
}
